/**
 * carmakesMap.js — carmakes reference values → this app's field values.
 *
 * Extracted from VehiclePickerModal.jsx so there is one copy in the reactor.
 *
 * MIRRORED SERVER-SIDE at node/helpers/carmakesMap.js, which
 * GET /enrichment/suggestions uses. Both must reach identical conclusions from
 * identical input: a vehicle enriched from the Inventory screen has to end up
 * with exactly the values the picker would have written had the wizard been
 * used in the first place. If the two copies drift, the same car gets two
 * different Drive values depending on which door it came through.
 * node/tests/p60-vehicle-data-quality.mjs pins every case below by literal
 * example so a change made in one place and not the other fails a test.
 */

// carmakes spells hybrids as a combination ("Petrol / electricity") and never
// uses the word "hybrid", so the combination tests MUST run before the plain
// petrol/diesel ones — otherwise every hybrid silently lands on Petrol.
export function mapFuelType(raw) {
  if (!raw) return "";
  const lower = raw.toLowerCase();
  const petrol   = lower.includes("gasoline") || lower.includes("petrol");
  const diesel   = lower.includes("diesel");
  // "electricity" covers itself and "electric"
  const electric = lower.includes("electric");

  if (electric && petrol) return "Hybrid (Petrol)";
  if (electric && diesel) return "Hybrid (Diesel)";
  if (electric)           return "Electric";
  // A source that says "hybrid" outright but names no second fuel
  if (lower.includes("hybrid")) return "Hybrid";
  if (petrol) return "Petrol";
  if (diesel) return "Diesel";
  // Hydrogen, LPG, CNG and natural gas have no home in the FuelType enum —
  // leave blank so the user picks rather than recording something wrong.
  return "";
}

export function mapTransmission(raw) {
  if (!raw) return "";
  const s = raw.trim();

  // Already compact: 6AT, 10DCT, 5MT, 7CVT, etc.
  if (/^\d+(AT|MT|CVT|DCT)$/i.test(s)) return s.toUpperCase();
  if (/^CVT$/i.test(s))               return "CVT";
  if (/^DCT$/i.test(s))               return "DCT";
  if (/^(AT|automatic)$/i.test(s))    return "AT";
  if (/^MT$/i.test(s))                return "MT";

  // "N-speed <type>" — covers word-form entries
  const m = s.match(/^(\d+)-speed\s+(automatic|manual|dct|dual[- ]?clutch|cvt)$/i);
  if (m) {
    const n    = parseInt(m[1], 10);
    const type = m[2].toLowerCase();
    if (type === "cvt")                          return n <= 1 ? "CVT"  : `${n}CVT`;
    if (type === "automatic")                    return n <= 1 ? "AT"   : `${n}AT`;
    if (type === "manual")                       return n <= 1 ? "MT"   : `${n}MT`;
    if (type === "dct" || type.includes("dual")) return `${n}DCT`;
  }

  // Fallback: keyword scan for free-form strings
  const lower = s.toLowerCase();
  if (lower.includes("cvt"))       return "CVT";
  if (lower.includes("dct") || lower.includes("dual")) return "DCT";
  if (lower.includes("automatic")) return "AT";
  if (lower.includes("manual"))    return "MT";
  return "";
}

// Matches anywhere in the string, not just at the start: the source mixes bare
// codes ("FWD") with compound labels ("RWD/Rear-Wheel Drive"), and startsWith
// would drop anything prefixed, e.g. "Part-time 4WD" or "Permanent AWD".
// 4WD is tested before AWD so "4WD/4-Wheel Drive/4x4" can't be caught by a
// stray "awd" substring, and AWD absorbs 6WD as the closest enum member.
export function mapDriveType(raw) {
  if (!raw) return "";
  const lower = raw.toLowerCase();
  if (lower.includes("4wd") || lower.includes("4-wheel") || lower.includes("4x4")) return "4WD";
  if (lower.includes("fwd") || lower.includes("front"))                           return "FWD";
  if (lower.includes("rwd") || lower.includes("rear"))                            return "RWD";
  if (lower.includes("awd") || lower.includes("6wd") || lower.includes("all-wheel")) return "AWD";
  return "";
}

export function sanitizeCode(raw) {
  if (!raw) return "";
  return raw.trim().replace(/\s*-\s*/g, "-");
}

/**
 * The chassis code inside a carmakes `model_code`.
 *
 *   'DBA-NZE141G' → 'NZE141G'
 *   'NCP131'      → 'NCP131'
 *   'BB-MC-1'     → 'MC1'
 *
 * carmakes stores codes with the Japanese emissions/era prefix attached —
 * `DBA-`, `3BA-`, `TA-` — but the chassis number stamped on the car carries only
 * the part after it. Measured on production 2026-08-11: of 12,497 distinct model
 * codes, 12,479 contain a hyphen and EVERY first segment is 4 characters or
 * shorter, so "everything after the first hyphen" is a rule rather than a guess.
 * The 18 without a hyphen are already bare.
 *
 * This is node/helpers/chassisResolve.js `chassisCodeFromModelCode`, ported so
 * the browser can offer the code as a prefix to type a serial onto. The two are
 * character-for-character the same function on purpose: the server resolves specs
 * by matching a stored chassis against these keys, so a browser that composed the
 * prefix differently would build chassis numbers the server could not read back.
 */
export function bareModelCode(modelCode) {
  if (modelCode == null) return "";
  const raw = String(modelCode);
  const hyphen = raw.indexOf("-");
  const tail = hyphen >= 0 ? raw.slice(hyphen + 1) : raw;
  return tail.toUpperCase().replace(/[^A-Z0-9]/g, "");
}

